import type { FunctionComponent } from 'react';

import { getMemberId } from '@trello/authentication';
import { useIsMemberOfOrganization } from '@trello/business-logic-react/organization';
import { LazyDiscoveryAdPopperContainer } from '@trello/discovery-ads/LazyDiscoveryAdPopperContainer';
import { intl } from '@trello/i18n';
import { useEnterpriseId, useWorkspaceId } from '@trello/id-context';
import { getTestId, type WorkspaceSwitcherTestIds } from '@trello/test-ids';

import { useDocumentTitle } from 'app/src/components/DocumentTitle';
import { TeamTemplatePicker } from 'app/src/components/TemplatePicker';
import { WorkspacePageHeader } from 'app/src/components/WorkspacePages/WorkspacePageHeader';
import { WorkspacePageSpinner } from 'app/src/components/WorkspacePages/WorkspacePageSpinner';
import { WorkspaceBoardsFilter } from './WorkspaceBoardsFilter';
import { WorkspaceBoardsListSection } from './WorkspaceBoardsListSection';
import { useWorkspaceBoardsViewQuery } from './WorkspaceBoardsViewQuery.generated';

import styles from './WorkspaceBoardsView.less';

export const WorkspaceBoardsView: FunctionComponent = () => {
  const memberId = getMemberId();
  const workspaceId = useWorkspaceId()!;
  const idEnterprise = useEnterpriseId();

  const isWorkspaceMember = useIsMemberOfOrganization({
    idMember: memberId,
    idOrganization: workspaceId,
  });

  const { data, loading } = useWorkspaceBoardsViewQuery({
    variables: { id: workspaceId },
    waitOn: ['MemberHeader'],
  });

  useDocumentTitle(
    data?.organization?.displayName
      ? `${data.organization.displayName} | ${intl.formatMessage({
          id: 'templates.workspace_navigation.boards',
          defaultMessage: 'Boards',
          description: 'Title for workspace boards page',
        })}`
      : intl.formatMessage({
          id: 'templates.workspace_navigation.boards',
          defaultMessage: 'Boards',
          description: 'Title for workspace boards page',
        }),
  );

  if (loading) {
    return <WorkspacePageSpinner testId={getTestId('spinner')} />;
  }

  return (
    <main className={styles.workspaceBoardsPageLayout}>
      <WorkspacePageHeader
        sticky
        title={intl.formatMessage({
          id: 'templates.workspace_page.boards',
          defaultMessage: 'Boards',
          description: 'Boards page title',
        })}
      >
        <section className={styles.templatePicker}>
          <TeamTemplatePicker orgId={workspaceId} isModernizedBoardPage />
        </section>

        <section>
          <WorkspaceBoardsFilter orgId={workspaceId} />
        </section>
      </WorkspacePageHeader>

      <section
        className={styles.boardTileContainer}
        data-testid={getTestId<WorkspaceSwitcherTestIds>(
          'workspace-boards-list-section',
        )}
      >
        <WorkspaceBoardsListSection
          orgId={workspaceId}
          isWorkspaceMember={isWorkspaceMember}
        />
      </section>
      {/* Enterprise self-serve experiment */}
      <LazyDiscoveryAdPopperContainer
        source="workspaceBoardsScreenBottomRight"
        oneTimeMessage="1p-ads-enterprise-self-serve"
        enterpriseId={idEnterprise || ''}
        workspaceId={workspaceId}
      />
    </main>
  );
};
